class ApiService {
      constructor() {
        this.baseUrl = process.env.REACT_APP_API_BASE || "/api";
      }

      async register(username, email, password, role="user") {
        try {
            const url = `${this.baseUrl}/user/register`;
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ username, email, password, role }),
            });

            if (!response.ok) {
                let response_message;
                try {
                    response_message = await response.json();
                } catch (e) {
                    response_message = { detail: `Registration failed with status ${response.status}` };
                }
                throw new Error(response_message.detail || `Registration failed with status ${response.status}`);
            }

            return await response.json();

        } catch (error) {
            console.error("Error during registration:", error);
            throw error;
        }
    }
    async login(email, password) {
        try {
            const url = `${this.baseUrl}/user/login`;
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: `Login failed with status ${response.status}` }));
                throw new Error(errorData.detail || `Login failed with status ${response.status}`);
            }

            const data = await response.json();
            if (data.token) {
                localStorage.setItem('token', data.token);
            }
            return data;
        } catch (error) {
            console.error("Error logging in:", error);
            throw error;
        }
    }
  
    logout() {
        localStorage.removeItem('token');
    }

      async updateGeom(userId, geomId, updatedGeom) {
        try {
          const url = `${this.baseUrl}/geom/user/${userId}/${geomId}`;
          const response = await fetch(url, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatedGeom),
          });
    
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
    
          return await response.json();
        } catch (error) {
          console.error("Error updating geometry:", error);
          throw error;
        }
      }

      async getGeom(userId, geomId) {
          try {
            const url = `${this.baseUrl}/geom/user/${userId}/${geomId}`;
            const response = await fetch(url, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            });
      
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
      
            return await response.json(); // Parse JSON response
          } catch (error) {
            console.error("Error fetching geometry:", error);
            throw error;
          }
        }

      async getGlobalLayers(){
        const token = localStorage.getItem('token');
        const url = `${this.baseUrl}/layers`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json(); // Parse JSON response
      }

      async getLayerValueByCoords(layerName, lon, lat, mapContext) {
        try {
            const url = new URL(`${this.baseUrl}/layers/query`, window.location.origin);
            url.searchParams.append('layer_name', layerName);
            url.searchParams.append('lon', lon);
            url.searchParams.append('lat', lat);
            url.searchParams.append('bbox', mapContext.bbox);
            url.searchParams.append('width', mapContext.width);
            url.searchParams.append('height', mapContext.height);
            url.searchParams.append('x', mapContext.x);
            url.searchParams.append('y', mapContext.y);

            const token = localStorage.getItem('token');
    
            const response = await fetch(url.toString(), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });
    
            if (!response.ok) {
                const error = new Error(`HTTP error! status: ${response.status}`);
                error.status = response.status;
                const errorBody = await response.json().catch(() => ({ detail: 'Failed to parse error body' }));
                error.body = errorBody;
                throw error;
            }
            return await response.json();
        } catch (error) {
            console.error(`Error fetching layer value for ${layerName}:`, error);
            throw error;
        }
      }

      async uploadTiff(formData) {
        const token = localStorage.getItem('token');
        // Assuming the backend is running on port 8081 as per docker-compose
        const url = new URL(`${this.baseUrl}/layers/upload-tiff`, window.location.origin);
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${token}`
            },
            body: formData,
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw errorData;
        }
        
        return await response.json();
    }

      async fetchRequests() {
        const token = localStorage.getItem('token');
        const url = `${this.baseUrl}/layers/fetch_requests`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
      }

      async getPixelValues(layerName, bbox) {
        try {
            const url = `${this.baseUrl}/layers/pixel-values`;
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ layer_name: layerName, bbox: bbox }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching pixel values:", error);
            throw error;
        }
      }

      async submitPixelAnalysis(payload) {
        try {
            const token = localStorage.getItem('token');
            const url = `${this.baseUrl}/layers/submit-analysis`;
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || "Analysis submission failed");
            }
            return await response.json();
        } catch (error) {
            console.error("Error submitting analysis:", error);
            throw error;
        }
      }

      async fetchPixelAnalyses() {
        const token = localStorage.getItem('token');
        const url = `${this.baseUrl}/layers/fetch_analyses`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
      }
  }
  
  export default new ApiService();